import assert from "node:assert";

import type { GraphQLError } from "graphql";
import pluralize from "pluralize";
import type { CloseEvent, ErrorEvent } from "ws";

import { GGTError, IsBug } from "../output/report.ts";
import { sprint } from "../output/sprint.ts";
import { uniq } from "../util/collection.ts";
import { isCloseEvent, isError, isErrorEvent, isGraphQLErrors, isString, isStringArray } from "../util/is.ts";
import { serializeError } from "../util/object.ts";
import { isRetryableNetworkErrorCode } from "../util/retry.ts";
import type { GraphQLOperation } from "./edit/operation.ts";
import { getOperationName } from "./edit/operation.ts";

export class ClientError extends GGTError {
  public isBug: IsBug;

  readonly operationName: string | undefined;

  override cause: string | string[] | Error | readonly GraphQLError[] | CloseEvent | ErrorEvent;

  constructor(operation: GraphQLOperation, cause: unknown, isBug?: IsBug) {
    super("An error occurred while communicating with Gadget");

    this.operationName = getOperationName(operation);

    if (isBug) {
      this.isBug = isBug;
    } else {
      this.isBug = IsBug.MAYBE;
    }

    // ErrorEvent and CloseEvent aren't serializable, so we reconstruct
    // them into an object. We discard the `target` property because
    // it's large and not that useful
    if (isErrorEvent(cause)) {
      this.cause = {
        type: cause.type,
        message: cause.message,
        error: serializeError(cause.error),
      } as ErrorEvent;
    } else if (isCloseEvent(cause)) {
      this.cause = {
        type: cause.type,
        code: cause.code,
        reason: cause.reason,
        wasClean: cause.wasClean,
      } as CloseEvent;
    } else {
      assert(
        isString(cause) || isStringArray(cause) || isError(cause) || isGraphQLErrors(cause),
        "cause must be a string, Error, GraphQLError[], CloseEvent, or ErrorEvent",
      );
      this.cause = cause;
    }
  }

  override render(): string {
    let body: string;

    switch (true) {
      case isGraphQLErrors(this.cause): {
        const errors = uniq(this.cause.map((x) => x.message));
        body = sprint`
          Gadget responded with the following ${pluralize("error", errors.length, false)}:

            ${errors.map((e) => `• ${e}`).join("\n")}
        `;
        break;
      }
      case isCloseEvent(this.cause):
        body = "The connection to Gadget closed unexpectedly.";
        break;
      case (isError(this.cause) && isRetryableNetworkErrorCode(this.cause)) ||
        (isErrorEvent(this.cause) && isRetryableNetworkErrorCode(this.cause.error)):
        body = "Please check your internet connection and try again.";
        break;
      case isErrorEvent(this.cause) || isError(this.cause):
        body = this.cause.message;
        break;
      case isStringArray(this.cause):
        if (this.cause.length === 1) {
          body = String(this.cause[0]);
        } else {
          body = this.cause.join(", ");
        }
        break;
      default:
        body = this.cause;
        break;
    }

    return sprint`
      ${this.message}${this.operationName ? ` (running "${this.operationName}")` : ""}

      ${body}
    `;
  }
}

export class AuthenticationError extends ClientError {
  constructor(operation: GraphQLOperation) {
    super(
      operation,
      "Request authentication failed due to the session expiring while running the command. Please sign-in again.",
      IsBug.NO,
    );
  }
}
